import React, { useState } from 'react';
import { Stethoscope, Smile, HeartPulse, Microscope, Scissors, Syringe, ArrowRight, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '@/hooks/useLang';
import { buildLangPath } from '@/utils/routeBuilders';

const Services = () => {
  const { lang } = useLang();
  const { t } = useTranslation('home');
  const [activeService, setActiveService] = useState<number | null>(null);

  const services = [
    {
      icon: <div className="pl-0">
              <img 
                src="/images/Implantology3.png" 
                alt={t('services.items.implantology.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.implantology.title'),
      description: t('services.items.implantology.description'),
      url: buildLangPath(lang, '/implantology')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/ENDODONTICS.png" 
                alt={t('services.items.endodontics.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.endodontics.title'),
      description: t('services.items.endodontics.description'),
      url: buildLangPath(lang, '/endodontics')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/Oral Surgery.png" 
                alt={t('services.items.oral-surgery.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.oral-surgery.title'),
      description: t('services.items.oral-surgery.description'),
      url: buildLangPath(lang, '/oral-surgery')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/PERIODONTOLOGY.png" 
                alt={t('services.items.periodontology.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.periodontology.title'),
      description: t('services.items.periodontology.description'),
      url: buildLangPath(lang, '/periodontology')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/ORTHODONTICS.png" 
                alt={t('services.items.orthodontics.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.orthodontics.title'),
      description: t('services.items.orthodontics.description'),
      url: buildLangPath(lang, '/orthodontics')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/BONDING.png" 
                alt={t('services.items.bonding.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.bonding.title'),
      description: t('services.items.bonding.description'),
      url: buildLangPath(lang, '/bonding')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/PROSTHETICS.png" 
                alt={t('services.items.prosthetics.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.prosthetics.title'),
      description: t('services.items.prosthetics.description'),
      url: buildLangPath(lang, '/prosthetics')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/CARIESOLOGY.png" 
                alt={t('services.items.cariesology.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.cariesology.title'),
      description: t('services.items.cariesology.description'),
      url: buildLangPath(lang, '/cariesology')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/TEETH WHITENING.png" 
                alt={t('services.items.teeth-whitening.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.teeth-whitening.title'),
      description: t('services.items.teeth-whitening.description'),
      url: buildLangPath(lang, '/teeth-whitening')
    },
    {
      icon: <div className="pl-0">
              <img 
                src="/images/ENTAL TOURISM.png" 
                alt={t('services.items.dental-tourism.imageAlt')} 
                className="w-14 h-14 object-contain -ml-3" 
              />
            </div>,
      title: t('services.items.dental-tourism.title'),
      description: t('services.items.dental-tourism.description'),
      url: buildLangPath(lang, '/dental-tourism')
    }
  ];

  const handleServiceHover = (index: number) => {
    setActiveService(index);
  };

  const handleServiceLeave = () => {
    setActiveService(null);
  };

  return (
    <section id="services" className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-dental-light/5 to-white"></div>
      <div className="absolute top-20 right-0 w-80 h-80 bg-dental-light/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-60 h-60 bg-dental-coral/5 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/3 w-40 h-40 bg-dental-mint/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4">
          <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-gradient-primary inline-block relative after:content-[''] after:absolute after:-bottom-2 after:left-1/4 after:right-1/4 after:h-1 after:bg-dental-tertiary after:rounded-full reveal" data-direction="up">
            {t('services.heading')}
          </h2>
          <p className="text-xl text-dental-dark/80 max-w-3xl mx-auto reveal" data-direction="up">
            {t('services.description')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index} 
              className="service-card group"
              onMouseEnter={() => handleServiceHover(index)}
              onMouseLeave={handleServiceLeave}
              onFocus={() => handleServiceHover(index)}
              onBlur={handleServiceLeave}
            >
              <div className="relative z-10">
                <div className="mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-display font-bold mb-3 text-dental-dark group-hover:text-dental transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-dental-dark/70 mb-4">
                  {service.description}
                </p>
                {service.url ? (
                  <Link 
                    to={service.url} 
                    className="inline-flex items-center text-dental font-medium hover:text-dental-dark transition-colors duration-300"
                  >
                    {t('services.learnMore')}
                    <ArrowRight size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                ) : (
                  <a 
                    href="#contact" 
                    className="inline-flex items-center text-dental font-medium hover:text-dental-dark transition-colors duration-300"
                  >
                    {t('services.contactCta')}
                    <ArrowRight size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                )}
              </div>
              
              {/* Animated highlight for active service */}
              {activeService === index && (
                <div className="absolute inset-0 border-2 border-dental rounded-2xl animate-pulse-gentle pointer-events-none"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
